import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './Portfolio.module.css';
import work1 from '../../assets/work1.jpeg';
import work2 from '../../assets/work2.jpeg';
import work3Video from '../../assets/work3.mp4';
import work4 from '../../assets/work4.jpeg';
import work5 from '../../assets/work5.jpeg';

const categories = ['All', 'Murals', 'Canvas', 'Textile'];

const works = [
  {
    id: 1,
    type: 'image',
    src: work1,
    title: 'Untitled Mural I',
    category: 'Murals',
    medium: 'Wall paint on gypsum',
    year: '2024',
    size: 'large',
  },
  {
    id: 2,
    type: 'image',
    src: work2,
    title: 'Canvas Study II',
    category: 'Canvas',
    medium: 'Acrylic on canvas',
    year: '2024',
    size: 'medium',
  },
  {
    id: 3,
    type: 'video',
    src: work3Video,
    title: 'Process in Motion',
    category: 'Murals',
    medium: 'Video documentation',
    year: '2024',
    size: 'large',
  },
  {
    id: 4,
    type: 'image',
    src: work4,
    title: 'Surface Pattern III',
    category: 'Textile',
    medium: 'Digital & print',
    year: '2023',
    size: 'medium',
  },
  {
    id: 5,
    type: 'image',
    src: work5,
    title: 'Commissioned Work IV',
    category: 'Canvas',
    medium: 'Oil on canvas',
    year: '2023',
    size: 'medium',
  },
];

export default function Portfolio() {
  const [filter, setFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  const filtered = filter === 'All' ? works : works.filter(w => w.category === filter);

  return (
    <section id="portfolio" className={styles.portfolio} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
        >
          <span className={styles.sectionLabel}>
            <span className={styles.labelLine} /> Selected Work
          </span>
          <h2 className={styles.heading}>
            A Portfolio of <em>Living Art</em>
          </h2>
          <p className={styles.subtext}>
            Each piece is born from observation, emotion, and a deep connection to the environments
            that shaped the artist — from Istanbul to Sydney to Vancouver.
          </p>
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          className={styles.filters}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {categories.map(cat => (
            <button
              key={cat}
              className={`${styles.filterBtn} ${filter === cat ? styles.filterActive : ''}`}
              onClick={() => setFilter(cat)}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Grid */}
        <motion.div className={styles.grid} layout>
          <AnimatePresence>
            {filtered.map((work, i) => (
              <motion.div
                key={work.id}
                className={`${styles.item} ${styles[`item${work.size}`]}`}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                onClick={() => setLightbox(work)}
              >
                {work.type === 'video' ? (
                  <video
                    src={work.src}
                    className={styles.media}
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img src={work.src} alt={work.title} className={styles.media} />
                )}
                <div className={styles.overlay}>
                  <div className={styles.overlayContent}>
                    <span className={styles.workCat}>{work.category}</span>
                    <h3 className={styles.workTitle}>{work.title}</h3>
                    <p className={styles.workMeta}>{work.medium} · {work.year}</p>
                    <span className={styles.viewBtn}>View</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        <motion.div
          className={styles.cta}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <a
            href="https://bookanartist.co/artist/artist-for-hire/13184"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaBtn}
          >
            Commission a Custom Piece
          </a>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className={styles.lightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.div
              className={styles.lightboxInner}
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 30 }}
              onClick={e => e.stopPropagation()}
            >
              {lightbox.type === 'video' ? (
                <video
                  src={lightbox.src}
                  className={styles.lightboxMedia}
                  controls
                  autoPlay
                  muted
                />
              ) : (
                <img src={lightbox.src} alt={lightbox.title} className={styles.lightboxMedia} />
              )}
              <div className={styles.lightboxInfo}>
                <span className={styles.lightboxCat}>{lightbox.category}</span>
                <h3 className={styles.lightboxTitle}>{lightbox.title}</h3>
                <p className={styles.lightboxMeta}>{lightbox.medium} · {lightbox.year}</p>
              </div>
              <button className={styles.closeBtn} onClick={() => setLightbox(null)} aria-label="Close">✕</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
